import { Badge, Box, IconButton } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { isAndroid, isBrowser, isIOS } from "react-device-detect";
import { deleteAPIAsync, getAPIAsync, postAPIAsync, putAPIAsync } from "../api/apiCalls";
import Provider from "../api/provider";
import { CHAT_FOOTER_START_HEIGHT, icon, IMAGE_RESIZE_PATH, isNullOrEmpty, restrictMaxLength, SELLER, SELLER_SITE_PATH, SEND_HI_HTML, SEND_THUMB_HTML, SIZE, STORE_BUCKET_NAME, StoreStats, UniqueID, USER_BUCKET_NAME, UserStats, WEBSITE_PATH, windowPostMessage } from "../common/common";
import { replaceAllData } from "../common/commonJS";
import { Decrypt, Encrypt } from "../common/reacte2ee";
import { resource } from "../common/resource";
import BlockedAccount from "../components/chat/blockedAccount";
import ChatFooter from "../components/chat/chatFooter";
import ChatHeader from "../components/chat/chatHeader";
import ChatMessage from "../components/chat/chatMessage";
import MessageAccept from "../components/chat/messageAccept";
import CreateMenuHeader from "../components/headers";
import CreateMenu from "../components/menu";
import { blockMenuItem, copyMenuItem, deleteMenuItem, MenuItems, muteMenuItem, optionsMenuItem, pinMenuItem, replyMenuItem, unblockMenuItem, unmuteMenuItem, unpinMenuItem } from "../components/menuItems/chatMenuItems";
import ChatSkeleton from "../components/skeletonLoaders/chatSkeleton";
import { ReceiverDetailsContext } from "../contexts/receiverDetailsContext";
import { Connection } from "../models/connector";
import { MessageData, MessagePostDetails, Reactions, URLDetails, User } from "../models/messaging";
import { EntityPostDetails, EntityPostInfo } from "../models/posts";
import "../styles/chat.css";

interface ChatWindowProps {
  messageConnection?: Connection;
  userConnection?: Connection;
  conversationID: string;
  senderID: string;
  receiverID: string;
  senderProfileDetails: User;
  chatID: string;
  onFollowStatusUpdate: any;
  ucnum: string;
}

const ChatWindow = (props: ChatWindowProps) => {
  const { chatID, conversationID, ucnum, senderID, receiverID, senderProfileDetails, messageConnection, userConnection } = props;
  const { receiverDetails, setReceiverDetails } = useContext<any>(ReceiverDetailsContext);

  const blockedAccount: boolean =
    (!SELLER && receiverDetails.entityType === 1 && receiverDetails.followingStatus === UserStats.USER_HAS_BLOCKED) ||
    (receiverDetails.entityType === 2 && receiverDetails.followingStatus === StoreStats.LOGGED_IN_USER_HAS_BLOCKED_THE_STORE) ||
    (SELLER && receiverDetails.followingStatus === StoreStats.STORE_HAS_BLOCKED_THE_USER);

  const adminIsBlocked: boolean =
    (!SELLER && receiverDetails.entityType === 1 && receiverDetails.followingStatus === UserStats.USER_HAS_BLOCKED_THE_LOGGED_IN_USER) ||
    (receiverDetails.entityType === 2 && receiverDetails.followingStatus === StoreStats.STORE_HAS_BLOCKED_THE_USER) ||
    (SELLER && receiverDetails.followingStatus === StoreStats.LOGGED_IN_USER_HAS_BLOCKED_THE_STORE);

  receiverDetails.isMuted && (senderProfileDetails.hasMuted = true);

  // #region Defined usestates
  const [connectivityStatus, setConnectivityStatus] = useState<boolean | null | undefined>(senderProfileDetails.connectivityStatus);
  const [chatFooterHeight, setChatFooterHeight] = useState<number>(CHAT_FOOTER_START_HEIGHT);
  const [chatOptions, setChatOptions] = useState<null | HTMLElement>(null);
  const [isMinimized, setIsMinimized] = useState<boolean>(false);
  const [chatSelected, setChatSelected] = useState<boolean>(false);
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [newMessageCount, setNewMessageCount] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const [messages, setMessages] = useState<MessageData[]>([]);
  const [messageLimitReached, setMessageLimitReached] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(true);
  const [isChatAtBottom, setIsChatAtBottom] = useState<boolean>(true);
  const [reactionEmojiMessageID, setReactionEmojiMessageID] = useState<string>("");
  const [selectedReply, setSelectedReply] = useState<MessageData | null>(null);
  const [selectedMessages, setSelectedMessages] = useState<MessageData[]>([]);
  const [mobMenuOptions, setMobMenuOptions] = useState<any>([]);
  // #endregion

  const scrollRef: any = useRef<boolean>();
  const messagesRef: any = useRef<MessageData[]>([]);
  const isChatAtBottomRef: any = useRef<boolean>(true);
  const shareDialogRef: any = useRef<any>();
  const currentMessage: any = useRef<MessageData>(null);
  const isLoadingMore: any = useRef<boolean>(false);
  const lastScrollElement: any = useRef<any>();
  const lastScrollPosition: any = useRef<number>(-1);
  const storeMessageCount: any = useRef<number>(0);
  const chatOptionsItems: any = useRef<Array<MenuItems>>([]);
  const chatOptionsRef: any = useRef<any>();
  const chatMenuHeaderRef: any = useRef<any>();
  const chatMessageRef: any = useRef<any>();
  const chatFooterRef: any = useRef<any>();
  const chatWarningRef: any = useRef<any>();
  const chatReactionRef: any = useRef<any>();
  const chatLoadRef: any = useRef<boolean>(true);
  const currFollowingStatusRef: any = useRef<number>(receiverDetails.followingStatus);
  const currFollowerCountRef: any = useRef<number>(receiverDetails.followingCount);

  // #region Common functions
  const handleWindowMessageReceived = (event: any) => {
    const sitePath: string = SELLER ? SELLER_SITE_PATH : WEBSITE_PATH;
    if (event.origin && event.origin.includes(sitePath.slice(0, -1))) {
      const data = event.data;
      switch (data.type) {
        case "changeConnectivityStatus":
          setConnectivityStatus(data.status);
          break;
        case "deleteMessage":
          if (data.messageType === 1) {
            deleteMessage(data.messageID, senderID, () => {
              deleteMessage(data.messageID, decodeURIComponent(receiverID), () => DeleteMessageSuccess(data));
            });
          } else {
            deleteMessage(data.messageID, senderID, () => DeleteMessageSuccess(data));
          }
          const params = {
            senderID: senderID,
            messageType: 4,
          };
          userConnection && userConnection.newMessage("true", JSON.stringify(params), decodeURIComponent(receiverID));
          userConnection && userConnection.newMessage("true", JSON.stringify(params), senderID);
          break;
        case "closeShare":
          chatFooterRef.current && chatFooterRef.current.closeShare();
          break;
        case "closeReaction":
          chatReactionRef.current && chatReactionRef.current.closeReactionDialog();
          break;
        case "closeEmoji":
          chatFooterRef.current && chatFooterRef.current.closeEmoji();
          chatFooterRef.current && chatFooterRef.current.closeShareMenu();
          chatMessageRef.current && chatMessageRef.current.hideMessageMenus();
          setChatOptions(null);
          break;
        case "userMuted":
          const currentReceiverMutedDetails: User = { ...receiverDetails };
          currentReceiverMutedDetails.isMuted = data.action;
          senderProfileDetails.hasMuted = data.action;
          setReceiverDetails(currentReceiverMutedDetails);
          break;
        case "unblock":
          unblockConversation();
          break;
        case "block":
          blockConversation();
          break;
        case "changeFollowStatus":
          const currentReceiverDetails: User = { ...receiverDetails };
          currentReceiverDetails.followingStatus = data.status;
          setReceiverDetails(currentReceiverDetails);
          currFollowingStatusRef.current = currentReceiverDetails.followingStatus;
          if (!data.noChange) {
            createAndSendMessage("", data.statusType);
          }
          break;
        case "deleteChat":
          deleteChat();
          break;
        case "updateConnectivityStatus":
          const currentReceiverDetailsStatus: User = { ...receiverDetails };
          currentReceiverDetailsStatus.followingStatus = currFollowingStatusRef.current;
          currentReceiverDetailsStatus.connectivityTstamp = data.action === "leave" ? new Date().getTime() : null;
          data.action === "mute" && (currentReceiverDetailsStatus.connectivityStatus = data.status);
          setReceiverDetails(currentReceiverDetailsStatus);
          break;
      }
    }
  };
  const handleWindowClick = (e: any) => {
    setChatSelected(false);
  };
  const setIsMessageLimitReached = () => {
    if (receiverDetails.entityType === 2 && storeMessageCount.current >= 3) {
      setMessageLimitReached(true);
      createAndSendMessage("", 18);
    }
  };
  const isAllowedToSendMessage = () => {
    if (SELLER) {
      return true;
    }
    if (receiverDetails.entityType === 1 && receiverDetails.followingStatus === UserStats.BOTH_USERS_FOLLOW) {
      return true;
    }
    if (receiverDetails.entityType === 2 && (storeMessageCount.current < 3 || receiverDetails.status === 1)) {
      return true;
    }
    return false;
  };
  // #endregion

  // #region Messaging functions
  const notifyMe = (message: any, messageType: number) => {
    senderProfileDetails.connectivityStatus && windowPostMessage({ type: "notificationPermission", messageType: messageType, message: message, senderName: receiverDetails.name }, WEBSITE_PATH);
  };
  const updateMessageStatus = (messageID: string, status: number) => {
    const allMessages = [...messages];
    const foundIndex: number = allMessages.findIndex((el) => el.messageID === messageID);
    if (foundIndex !== -1) {
      allMessages[foundIndex].messageStatus = status;
    }
    createAndSendMessage(status.toString(), 16, [], messageID);
    setMessages(allMessages);
  };
  const markAllMessagesRead = () => {
    const allMessages = [...messages];
    allMessages.forEach((k: MessageData) => {
      k.isRead = true;
    });
    setMessages(allMessages);
  };
  const signalRMessage = (messageData: MessageData, newChat: boolean = false) => {
    if (newChat) {
      const params = {
        senderDetails: senderProfileDetails,
        senderID: senderID,
        receiverDetails: receiverDetails,
        conversationID: conversationID,
        messageType: 1,
      };
      userConnection && userConnection.newMessage(JSON.stringify(messageData), JSON.stringify(params), decodeURIComponent(receiverID));
    }
    messageConnection && messageConnection.newMessage(JSON.stringify(messageData), senderID, conversationID);
  };
  const createAndSendMessage = (messageContent: string, messageType: number, reaction: Reactions[] = [], messageIDProp: string = "") => {
    const messageID: string = messageIDProp !== "" ? messageIDProp : UniqueID();
    const messageDetails: MessageData = {
      conversationID: conversationID,
      messageID: messageID,
      messageContent: Encrypt(messageContent),
      messageType: messageType,
      messageStatus: 0,
      recipientUcnum: decodeURIComponent(receiverID),
      isRead: false,
      deletedBy: "",
      sentAt: new Date().getTime(),
      reactions: reaction,
      parent: selectedReply ? [selectedReply] : undefined,
      isLast: messages.length === 0,
      notSent: !isAllowedToSendMessage(),
    };
    if (messageType === 1 || messageType === 2 || messageType === 3 || messageType === 4) {
      scrollRef.current = true;
      currentMessage.current = messageDetails;
      onRestartConversation();
      SELLER && onAcceptMessageClick();
      setMessages((prevMessages) => [messageDetails, ...prevMessages]);
      const params = {
        senderID: senderID,
        messageType: 4,
      };
      userConnection && userConnection.newMessage("true", JSON.stringify(params), decodeURIComponent(receiverID));
      userConnection && userConnection.newMessage("true", JSON.stringify(params), senderID);
    } else {
      signalRMessage(messageDetails, false);
    }
  };
  const onMessageReceived = (sentBy: string, currMessage: string) => {
    const parsedMessage: MessageData = JSON.parse(currMessage);
    switch (parsedMessage.messageType) {
      case 1: // Normal message with or without emojis received
      case 2: // Waving hand or Thumbs up received
      case 3: // Share Post received
      case 4: // Link received
        if (sentBy !== senderID && !parsedMessage.notSent) {
          // for receiver prepend message
          if (chatMessageRef.current) {
            lastScrollPosition.current = chatMessageRef.current.isEnd() ? -1 : chatMessageRef.current.getChatScrollPosition();
          }
          chatMessageRef.current.isEnd() && (scrollRef.current = true);
          setMessages((prevMessages) => [parsedMessage, ...prevMessages]);
          // highlight chatbox for the receiver
          if (isMinimized || (chatFooterRef.current && !chatFooterRef.current.isChatFocused())) {
            setChatSelected(true);
          }
          // mark read in the database and notify the recepient
          if (!isMinimized) {
            parsedMessage.recipientUcnum && markRead(parsedMessage.recipientUcnum);
            createAndSendMessage("", 5);
          }
          // add new message count
          setNewMessageCount(newMessageCount + 1);
          // send browser notification to the receiver
          if (!senderProfileDetails.hasMuted) {
            try {
              notifyMe(parsedMessage.messageContent, parsedMessage.messageType);
            } catch (e) {}
          }
        } else {
          const foundMessageData: MessageData | undefined = [...messages].find((el) => el.messageID === parsedMessage.messageID);
          if (!foundMessageData) {
            chatMessageRef.current.isEnd() && (scrollRef.current = true);
            setMessages((prevMessages) => [parsedMessage, ...prevMessages]);
          }
        }
        break;
      case 5: // Acknowledgement for receiver that message is read
        if (sentBy !== senderID) {
          markAllMessagesRead();
        }
        break;
      case 6: // Acknowledgement for receiver that sender is typing message
        if (sentBy !== senderID) {
          setIsTyping(Decrypt(parsedMessage.messageContent) !== "true");
        }
        break;
      case 7: // Acknowledgement for receiver that sender blocked the receiver
        if (sentBy !== senderID) {
          const currentReceiverDetails: User = { ...receiverDetails };
          currentReceiverDetails.followingStatus = SELLER ? StoreStats.LOGGED_IN_USER_HAS_BLOCKED_THE_STORE : receiverDetails.entityType === 2 ? StoreStats.STORE_HAS_BLOCKED_THE_USER : UserStats.USER_HAS_BLOCKED_THE_LOGGED_IN_USER;
          setReceiverDetails(currentReceiverDetails);
          currFollowingStatusRef.current = currentReceiverDetails.followingStatus;
        } else {
          const currentReceiverDetails: User = { ...receiverDetails };
          currentReceiverDetails.followingStatus = SELLER ? StoreStats.STORE_HAS_BLOCKED_THE_USER : receiverDetails.entityType === 2 ? StoreStats.LOGGED_IN_USER_HAS_BLOCKED_THE_STORE : UserStats.USER_HAS_BLOCKED;
          setReceiverDetails(currentReceiverDetails);
          currFollowingStatusRef.current = currentReceiverDetails.followingStatus;
          chatMessageRef.current && chatMessageRef.current.hideMessageWarning();
        }
        break;
      case 8: // Acknowledgement for receiver that sender unblocked the receiver
        if (sentBy !== senderID) {
          const currentReceiverDetails: User = { ...receiverDetails };
          currentReceiverDetails.followingStatus = SELLER || receiverDetails.entityType === 2 ? StoreStats.NEITHER_USER_IS_FOLLOWING : UserStats.NEITHER_USER_IS_FOLLOWING;
          setReceiverDetails(currentReceiverDetails);
          currFollowingStatusRef.current = currentReceiverDetails.followingStatus;
        } else {
          const currentReceiverDetails: User = { ...receiverDetails };
          currentReceiverDetails.followingStatus = SELLER || receiverDetails.entityType === 2 ? StoreStats.NEITHER_USER_IS_FOLLOWING : UserStats.NEITHER_USER_IS_FOLLOWING;
          setReceiverDetails(currentReceiverDetails);
          currFollowingStatusRef.current = currentReceiverDetails.followingStatus;
          chatMessageRef.current && chatMessageRef.current.showMessageWarning();
          setChatFooterHeight(CHAT_FOOTER_START_HEIGHT);
        }
        break;
      case 9: // Acknowledgement for receiver that sender followed the receiver
        if (sentBy !== senderID) {
          const currentReceiverDetails: User = { ...receiverDetails };
          currentReceiverDetails.followingStatus = receiverDetails.entityType === 2 ? StoreStats.LOGGED_IN_USER_IS_FOLLOWING_THE_STORE : UserStats.USER_IS_FOLLOWING;
          setReceiverDetails(currentReceiverDetails);
          currFollowingStatusRef.current = currentReceiverDetails.followingStatus;
        }
        break;
      case 10: // Acknowledgement for receiver that sender followed back the receiver
        if (sentBy !== senderID) {
          const currentReceiverDetails: User = { ...receiverDetails };
          currentReceiverDetails.followingStatus = receiverDetails.entityType === 2 ? StoreStats.LOGGED_IN_USER_IS_FOLLOWING_THE_STORE : UserStats.BOTH_USERS_FOLLOW;
          setReceiverDetails(currentReceiverDetails);
          currFollowingStatusRef.current = currentReceiverDetails.followingStatus;
        }
        break;
      case 11: // Acknowledgement to change reaction of the receiver
        if (sentBy !== senderID) {
          if (parsedMessage.reactions) {
            const allReactionMessages: MessageData[] = [...messages];
            const currMessage: MessageData | undefined = allReactionMessages.find((el: any) => el.messageID === parsedMessage.messageID);
            if (currMessage) {
              currMessage.reactions = parsedMessage.reactions;
            }
            setMessages(allReactionMessages);
          }
        }
        break;
      case 12: // Acknowledement received for chat is muted
        const currentReceiverDetails: User = { ...receiverDetails };
        chatOptionsItems.current && (chatOptionsItems.current[1] = Decrypt(parsedMessage.messageContent) === "true" ? unmuteMenuItem(onUnmuteClick) : muteMenuItem(onMuteClick));
        if (sentBy !== senderID) {
          currentReceiverDetails.hasMuted = Decrypt(parsedMessage.messageContent) === "true";
          senderProfileDetails.isMuted = Decrypt(parsedMessage.messageContent) === "true";
        } else {
          senderProfileDetails.hasMuted = Decrypt(parsedMessage.messageContent) === "true";
          currentReceiverDetails.isMuted = Decrypt(parsedMessage.messageContent) === "true";
        }

        setReceiverDetails(currentReceiverDetails);
        break;
      case 13: // Acknowledgement to change status for the user by store
        if (sentBy !== senderID) {
          const currentReceiverDetails: User = { ...receiverDetails };
          currentReceiverDetails.status = 1;
          setReceiverDetails(currentReceiverDetails);
        }
        break;
      case 14: // Acknowledement for message deleted by sender
        if (sentBy !== senderID) {
          const allMessages = [...messagesRef.current];
          const foundMessage: MessageData | undefined = allMessages.find((el) => el.messageID === parsedMessage.messageID);
          if (foundMessage) {
            foundMessage.deletedBy = "Both";
          }
          scrollRef.current = false;
          setMessages(allMessages);
        }
        break;
      case 15: // Acknowledgement to set pinned conversation
        const currentReceiverDetailsPin: User = { ...receiverDetails };
        currentReceiverDetailsPin.pinnedAt = Decrypt(parsedMessage.messageContent) === "true" ? new Date().getTime() : 0;
        setReceiverDetails(currentReceiverDetailsPin);
        if (sentBy === senderID) {
          chatOptionsItems.current && (chatOptionsItems.current[0] = Decrypt(parsedMessage.messageContent) === "true" ? unpinMenuItem(onUnpinClick) : pinMenuItem(onPinClick));
        }
        break;
      case 16: // Acknowledgement for updating message status for sender
        if (sentBy === senderID) {
          const allMessagesData = [...messages];
          const foundIndex: number = allMessagesData.findIndex((el) => el.messageID === parsedMessage.messageID);
          if (foundIndex !== -1) {
            allMessagesData[foundIndex].messageStatus = parseInt(Decrypt(parsedMessage.messageContent));
          }
          setMessages(allMessagesData);
        }
        break;
      case 17: // Acknowledgement for updating following status for sender
        if (sentBy === senderID && currFollowingStatusRef.current !== parseInt(Decrypt(parsedMessage.messageContent))) {
          const currentReceiverDetailsFollow: User = { ...receiverDetails };
          currentReceiverDetailsFollow.followingStatus = parseInt(Decrypt(parsedMessage.messageContent));
          setReceiverDetails(currentReceiverDetailsFollow);
          currFollowingStatusRef.current = currentReceiverDetailsFollow.followingStatus;
          chatWarningRef.current && chatWarningRef.current.showFollowingWarning();
        }
        break;
      case 18: // Acknowledgement for updating message limit reached for sender
        if (sentBy === senderID) {
          setMessageLimitReached(true);
        }
        break;
    }
  };
  messageConnection && (messageConnection.onMessageReceived = onMessageReceived);
  // #endregion

  // #region Chat window clicks
  const onChatMenuClick = (event: any) => {
    !isBrowser && windowPostMessage({ type: "pushState", pushPage: "chatOptions" }, WEBSITE_PATH);
    isMinimized && windowPostMessage({ type: "adjustHeight", isMinimized: !isMinimized, chatID: chatID }, WEBSITE_PATH);
    setIsMinimized(false);
    setChatOptions(event.currentTarget);
  };
  const onCloseChatMenu = () => {
    setChatOptions(null);
  };
  const onMinimizeChat = () => {
    windowPostMessage({ type: "adjustHeight", isMinimized: !isMinimized, chatID: chatID }, WEBSITE_PATH);
    if (isMinimized && newMessageCount > 0) {
      setIsChatAtBottom(chatMessageRef.current && chatMessageRef.current.isEnd());
    }
    setIsMinimized((isMinimized) => !isMinimized);
  };
  const onCloseChat = () => {
    windowPostMessage({ type: "closeChat", chatID: chatID }, WEBSITE_PATH);
    !isBrowser && windowPostMessage({ type: "refresh" }, WEBSITE_PATH);
  };
  const onMessageBoxChanged = (isMessageBoxEmpty: boolean) => {
    createAndSendMessage(isMessageBoxEmpty.toString(), 6);
    const params = {
      senderID: senderID,
      messageType: 3,
    };
    userConnection && userConnection.newMessage(isMessageBoxEmpty.toString(), JSON.stringify(params), decodeURIComponent(receiverID));
  };
  const onWavingHandClick = () => {
    createAndSendMessage(SEND_HI_HTML, 2);
  };
  const onThumbsUpClick = () => {
    chatFooterRef.current && chatFooterRef.current.closeEmoji();
    createAndSendMessage(SEND_THUMB_HTML, 2);
  };
  const onSendClick = () => {
    if (!restrictMaxLength(null, false)) {
      windowPostMessage({ type: "maxCharacterSnack", snackMsg: resource.messageIsTooLong }, WEBSITE_PATH);
      return;
    }
    const msgHtml: string | undefined = document.getElementById("messageBox")?.innerHTML;
    if (!isNullOrEmpty(msgHtml)) {
      chatFooterRef.current && chatFooterRef.current.closeEmoji();
      const linkDetails: URLDetails = chatFooterRef.current && chatFooterRef.current.getLinkDetails();
      if (linkDetails) {
        createAndSendMessage(JSON.stringify(linkDetails), 4);
      } else {
        createAndSendMessage(msgHtml ? msgHtml : "", 1);
      }

      chatFooterRef.current && chatFooterRef.current.setCaret();
      chatFooterRef.current && chatFooterRef.current.saveCaretPosition();
    }
    chatFooterRef.current && chatFooterRef.current.emptyTextBox();
  };
  const onBookmarkedPostClick = (epd: EntityPostInfo) => {
    const messageDetails: any = {
      postLink: `${WEBSITE_PATH}@${epd.postAdminDetails.displayUsername}/video/${epd.entityPostDetails.sysObjectID}`,
      postImage: `${IMAGE_RESIZE_PATH}${epd.entityPostDetails.postAttributes.entityMedia.folderPath}/${epd.entityType !== 1 ? epd.entityPostDetails.postAttributes.entityMedia.coverPosition : epd.entityPostDetails.postAttributes.entityMedia.filename.replace(".mp4", ".jpeg")}?preset=120`,
      postCaption: epd.entityPostDetails.caption,
      name: epd.postAdminDetails.name,
      profilePhotoColor: epd.postAdminDetails.profilePhotoColor,
      profilePhoto: `${IMAGE_RESIZE_PATH}${epd.entityType === 1 ? USER_BUCKET_NAME : STORE_BUCKET_NAME}/${epd.postAdminDetails.profilePhoto};preset=24`,
      eSysObjectID: epd.entityPostDetails.eSysObjectID,
    };
    createAndSendMessage(JSON.stringify(messageDetails), 3);
    shareDialogRef.current && shareDialogRef.current.closeShareDialog();
  };
  const onMyPostClick = (epd: EntityPostDetails) => {
    const messageDetails: MessagePostDetails = {
      postLink: `${WEBSITE_PATH}@${senderProfileDetails.displayUsername}/video/${epd.sysObjectID}`,
      postImage: `${IMAGE_RESIZE_PATH}${epd.postAttributes.entityMedia.folderPath}/${
        SELLER ? (epd.postAttributes.entityMedia.coverPosition ? epd.postAttributes.entityMedia.coverPosition : epd.postAttributes.entityMedia.filename.replace(".mp4", ".jpeg")) : epd.postAttributes.entityMedia.filename.replace(".mp4", ".jpeg")
      }?preset=120`,
      postCaption: epd.caption,
      name: senderProfileDetails.name,
      profilePhotoColor: senderProfileDetails.profilePhotoColor,
      profilePhoto: `${IMAGE_RESIZE_PATH}${SELLER ? STORE_BUCKET_NAME : USER_BUCKET_NAME}/${senderProfileDetails.profilePhoto};preset=24`,
      eSysObjectID: epd.eSysObjectID,
    };
    createAndSendMessage(JSON.stringify(messageDetails), 3);
    shareDialogRef.current && shareDialogRef.current.closeShareDialog();
  };
  const onMessageLongPress = (messageID: string, isTap: boolean = false) => {
    if (!isBrowser && ((isTap && selectedMessages.length > 0) || (!isTap && selectedMessages.length === 0)) && !isNullOrEmpty(messageID)) {
      if (selectedMessages.findIndex((el) => el.messageID === messageID) === -1) {
        const data = messages.find((el) => el.messageID === messageID);
        if (data) {
          if (data.deletedBy === "Both") {
            setSelectedMessages((prevSelectedMessages) => {
              return [data];
            });
          } else {
            setSelectedMessages((prevSelectedMessages) => {
              let newData = prevSelectedMessages.filter((el) => el.deletedBy !== "Both");
              return [...newData, data];
            });
          }
        }
      } else {
        let newArrData = selectedMessages.filter((el) => el.messageID !== messageID);
        setSelectedMessages(newArrData);
      }
    }
  };
  const onScrollEvent = (e: any) => {
    const $eTarget = e.currentTarget;
    if (!isMinimized) {
      if (chatMessageRef.current && chatMessageRef.current.isEnd()) {
        setNewMessageCount(0);
        isChatAtBottomRef.current = true;
        !isChatAtBottom && setIsChatAtBottom(true);
      } else {
        isChatAtBottomRef.current = false;
        isChatAtBottom && setIsChatAtBottom(false);
      }
    }
    if ($eTarget.scrollTop <= 0 && !isLoadingMore.current && chatLoadRef.current) {
      isLoadingMore.current = true;
      setOffset(offset + SIZE);
    }
  };
  const onScrollToBottomClick = () => {
    chatMessageRef.current.scrollChatToBottom(true);
  };
  const onCopyMessage = () => {
    if (selectedMessages.length > 0) {
      const messageContentArr: any = [];
      selectedMessages.forEach((singleData: MessageData) => {
        if (singleData.messageType === 1) {
          let userName = senderProfileDetails.name;
          if (singleData) {
            singleData.recipientUcnum === senderID && (userName = receiverDetails.name);
            messageContentArr.push(`${userName}: ${singleData?.messageContent}`);
          }
        }
      });
      navigator.clipboard.writeText(messageContentArr.join(" "));
      !isAndroid && windowPostMessage({ type: "snackMsg", snackMsg: resource.messageCopied }, WEBSITE_PATH);
      setSelectedMessages([]);
    }
  };
  const onReactionEmojiClick = (event: any) => {
    const backgroundImage: string = event.currentTarget.querySelector(".emoji-mart-emoji span").style.backgroundImage;
    const backgroundPosition: string = event.currentTarget.querySelector(".emoji-mart-emoji span").style.backgroundPosition;
    const reaction: any = {
      label: "",
      nodeString: `<img aria-label="${event.currentTarget.getAttribute("aria-label")}" ${
        isIOS ? `srcSet="" src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/wcAAgMBAhS+BpQAAAAASUVORK5CYII="` : `src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="`
      } width="16" height="16" style="${isIOS ? `object-fit: none;` : ``} background-size: 6100% 6100%; background-image:${replaceAllData(backgroundImage, `"`, "")}; background-position: ${backgroundPosition};" />`,
    };
    onUpdateReaction(reactionEmojiMessageID, "", reaction);
    chatFooterRef.current && chatFooterRef.current.closeEmoji();
    setReactionEmojiMessageID("");
  };
  const clearReactionEmojiMessageID = () => {
    setReactionEmojiMessageID("");
  };
  const clearSelectedMessages = () => {
    setSelectedMessages([]);
  };
  const onReplyClick = (messageDetails: MessageData) => {
    chatFooterRef.current && chatFooterRef.current.focus();
    setSelectedReply(messageDetails);
    setSelectedMessages([]);
  };
  const onReplyCloseClick = () => {
    setSelectedReply(null);
  };
  const onDeleteMessageClick = (messageDetails: MessageData) => {
    if (messageDetails.deletedBy !== "Both") {
      const within60Hours: boolean = new Date().getTime() - messageDetails.sentAt <= 216000000 && messageDetails.recipientUcnum === decodeURIComponent(receiverID);
      windowPostMessage({ ...messageDetails, type: "deleteMessage", chatID: chatID, conversationID: conversationID, within60Hours: within60Hours, isAdmin: messageDetails.recipientUcnum === decodeURIComponent(receiverID) }, WEBSITE_PATH);
      chatMessageRef.current && chatMessageRef.current.hideMessageMenus();
    } else {
      hardDeleteMessage(messageDetails.messageID, decodeURIComponent(senderID), () => onHardDeleteMessageSuccess(messageDetails));
    }
    setSelectedMessages([]);
  };
  const onChatOptionsClose = () => {
    setChatOptions(null);
  };
  const onMessageSingleClick = () => {
    chatFooterRef.current && chatFooterRef.current.closeEmoji();
  };
  // #endregion

  // #region API calls success failure
  const onAPIFailure = () => {
    windowPostMessage({ type: "snackMsg", snackMsg: resource.error }, WEBSITE_PATH);
  };
  const GetMessagesSuccess = (response: any) => {
    isLoadingMore.current = false;
    if (!isNullOrEmpty(response.data.messageResponse)) {
      lastScrollElement.current = chatMessageRef.current.getLastMessageElement();
      let historyMessages: MessageData[] = response.data.messageResponse;
      historyMessages = historyMessages.filter((el) => el.messageType !== 0);
      if (historyMessages.length < SIZE) {
        historyMessages[historyMessages.length - 1].isLast = true;
      }
      storeMessageCount.current = [...messages, ...historyMessages].length;
      setMessages((prevMessages) => [...prevMessages, ...historyMessages]);
      setIsMessageLimitReached();
    } else {
      chatLoadRef.current = false;
    }
    setLoader(false);
  };
  const GetMessagesFailure = () => {
    if (messages.length > 0) {
      let data: MessageData[] = [...messages];
      data[data.length - 1].isLast = true;
      setMessages(data);
    }

    isLoadingMore.current = false;
    chatLoadRef.current = false;
    setLoader(false);
  };
  const UnpinSuccess = () => {
    chatOptionsItems.current && (chatOptionsItems.current[0] = pinMenuItem(onPinClick));
    receiverDetails.pinnedAt = 0;
    onCloseChatMenu();
    windowPostMessage({ type: "snackMsg", snackMsg: resource.userUnPinned }, WEBSITE_PATH);
    createAndSendMessage("false", 15);
  };
  const PinSuccess = () => {
    chatOptionsItems.current && (chatOptionsItems.current[0] = unpinMenuItem(onUnpinClick));
    const currentReceiverDetails: User = { ...receiverDetails };
    currentReceiverDetails.pinnedAt = new Date().getTime();
    setReceiverDetails(currentReceiverDetails);
    onCloseChatMenu();
    windowPostMessage({ type: "snackMsg", snackMsg: resource.userPinned }, WEBSITE_PATH);
    createAndSendMessage("true", 15);
  };
  const MuteSuccess = () => {
    chatOptionsItems.current && (chatOptionsItems.current[1] = unmuteMenuItem(onUnmuteClick));
    const currentReceiverDetails: User = { ...receiverDetails };
    currentReceiverDetails.isMuted = true;
    senderProfileDetails.hasMuted = true;
    setReceiverDetails(currentReceiverDetails);
    onCloseChatMenu();
    windowPostMessage({ type: "snackMsg", snackMsg: resource.userMute }, WEBSITE_PATH);
    createAndSendMessage("true", 12);
  };
  const UnmuteSuccess = () => {
    chatOptionsItems.current && (chatOptionsItems.current[1] = muteMenuItem(onMuteClick));
    const currentReceiverDetails: User = { ...receiverDetails };
    currentReceiverDetails.isMuted = false;
    senderProfileDetails.hasMuted = false;
    setReceiverDetails(currentReceiverDetails);
    onCloseChatMenu();
    windowPostMessage({ type: "snackMsg", snackMsg: resource.userUnMute }, WEBSITE_PATH);
    createAndSendMessage("false", 12);
  };
  const DeleteSuccess = () => {
    if (!isBrowser) {
      windowPostMessage({ type: "refresh" }, WEBSITE_PATH);
      windowPostMessage({ type: "historyBack" }, WEBSITE_PATH);
    }
    onCloseChat();
  };
  const BlockSuccess = () => {
    windowPostMessage({ type: "snackMsg", snackMsg: resource.userBlock.replace("{0}", receiverDetails.name) }, WEBSITE_PATH);
    createAndSendMessage("", 7);
    const currentReceiverDetails: User = { ...receiverDetails };
    currentReceiverDetails.followingStatus = SELLER ? StoreStats.STORE_HAS_BLOCKED_THE_USER : receiverDetails.entityType === 2 ? StoreStats.LOGGED_IN_USER_HAS_BLOCKED_THE_STORE : UserStats.USER_HAS_BLOCKED;
    setReceiverDetails(currentReceiverDetails);
    currFollowingStatusRef.current = currentReceiverDetails.followingStatus;
    chatMessageRef.current && chatMessageRef.current.hideMessageWarning();
    onCloseChatMenu();
  };
  const UnblockSuccess = () => {
    windowPostMessage({ type: "snackMsg", snackMsg: resource.userUnBlock.replace("{0}", receiverDetails.name) }, WEBSITE_PATH);
    createAndSendMessage("", 8);
    const currentReceiverDetails: User = { ...receiverDetails };
    currentReceiverDetails.followingStatus = SELLER || receiverDetails.entityType === 2 ? StoreStats.NEITHER_USER_IS_FOLLOWING : UserStats.NEITHER_USER_IS_FOLLOWING;
    currentReceiverDetails.followers = currFollowerCountRef.current;
    setReceiverDetails(currentReceiverDetails);
    currFollowingStatusRef.current = currentReceiverDetails.followingStatus;
    chatMessageRef.current && chatMessageRef.current.showMessageWarning();
    setChatFooterHeight(CHAT_FOOTER_START_HEIGHT);
    onCloseChatMenu();
  };
  const FollowSuccess = (statusType: number) => {
    if (statusType === 1) {
      const currentReceiverDetails: User = { ...receiverDetails };
      currentReceiverDetails.followingStatus = receiverDetails.entityType === 1 ? UserStats.LOGGED_IN_USER_IS_FOLLOWING : StoreStats.LOGGED_IN_USER_IS_FOLLOWING_THE_STORE;
      setReceiverDetails(currentReceiverDetails);
      currFollowingStatusRef.current = currentReceiverDetails.followingStatus;
      createAndSendMessage(currentReceiverDetails.followingStatus.toString(), 17);
      createAndSendMessage("", 9);
    } else {
      const currentReceiverDetails: User = { ...receiverDetails };
      currentReceiverDetails.followingStatus = UserStats.BOTH_USERS_FOLLOW;
      setReceiverDetails(currentReceiverDetails);
      currFollowingStatusRef.current = currentReceiverDetails.followingStatus;
      createAndSendMessage(currentReceiverDetails.followingStatus.toString(), 17);
      createAndSendMessage("", 10);
    }
    const params = {
      senderID: senderID,
      messageType: 4,
    };
    userConnection && userConnection.newMessage(JSON.stringify(params), JSON.stringify(params), decodeURIComponent(receiverID));
  };
  const DeleteMessageSuccess = (messageDetails: any) => {
    const allMessages = [...messagesRef.current];
    const foundMessage: MessageData | undefined = allMessages.find((el) => el.messageID === messageDetails.messageID);
    if (foundMessage) {
      messageDetails.messageType === 1 && createAndSendMessage("", 14, [], foundMessage.messageID);
      foundMessage.deletedBy = messageDetails.messageType === 1 ? "Both" : senderID;
      scrollRef.current = false;
      setMessages(allMessages);
    }
  };
  const AcceptMessageSuccess = () => {
    const currentReceiverDetails: User = { ...receiverDetails };
    currentReceiverDetails.status = 1;
    setReceiverDetails(currentReceiverDetails);
    setChatFooterHeight(CHAT_FOOTER_START_HEIGHT);
    createAndSendMessage("", 13);
  };

  const onHardDeleteMessageSuccess = (messageDetails: any) => {
    const allMessages = [...messagesRef.current];
    const index: number = allMessages.findIndex((el) => el.messageID === messageDetails.messageID);
    const foundMessage: MessageData[] = allMessages.filter((el) => el.messageID !== messageDetails.messageID);
    setMessages(foundMessage);
    if (index === 0) {
      const updateParams: any = {
        conversationID: conversationID,
        lastActiveAt: 0,
        lastMessageId: allMessages.length === 1 ? null : allMessages[1].messageID,
      };
      putAPIAsync(Provider.URLS.BASE_URL, Provider.API_URLS.UpdateConversation, updateParams);
    }
  };
  // #endregion

  // #region API calls
  const onPinClick = () => {
    const params = {
      ucnum: senderID,
      pinRequest: [{ pinnedBy: senderID, conversationID: conversationID, pinnedAt: 0 }],
    };
    postAPIAsync(Provider.URLS.BASE_URL, Provider.API_URLS.PinConversation, params, PinSuccess, onAPIFailure);
  };
  const onUnpinClick = () => {
    const params = {
      ucnum: senderID,
      pinRequest: [{ pinnedBy: senderID, conversationID: conversationID }],
    };
    deleteAPIAsync(Provider.URLS.BASE_URL, Provider.API_URLS.UnpinConversation, params, UnpinSuccess, onAPIFailure);
  };
  const onMuteClick = () => {
    postAPIAsync(Provider.URLS.BASE_URL, Provider.API_URLS.InsertMute, { ucnum: senderID, mutedUsercodenum: [{ mutedUcnum: decodeURIComponent(receiverID), conversationID: conversationID }] }, MuteSuccess, onAPIFailure);
  };
  const onUnmuteClick = () => {
    deleteAPIAsync(Provider.URLS.BASE_URL, Provider.API_URLS.InsertMute, { ucnum: senderID, mutedUsercodenum: [{ mutedUcnum: decodeURIComponent(receiverID), conversationID: conversationID }] }, UnmuteSuccess, onAPIFailure);
  };
  const onDeleteClick = () => {
    //add popup
    windowPostMessage({ type: "userDeleteBlock", chatID: chatID }, WEBSITE_PATH);
  };
  const onBlockClick = () => {
    currFollowingStatusRef.current = receiverDetails.followingStatus;
    windowPostMessage({ type: "userBlockUnblocked", chatID: chatID, fullname: receiverDetails.name, username: receiverDetails.displayUsername, blockType: "block", callType: "chatwindow" }, WEBSITE_PATH);
  };
  const onUnblockClick = () => {
    currFollowerCountRef.current = receiverDetails.followers;
    windowPostMessage({ type: "userBlockUnblocked", chatID: chatID, fullname: receiverDetails.name, username: receiverDetails.displayUsername, blockType: "unblock", callType: "chatwindow" }, WEBSITE_PATH);
  };
  const onAcceptMessageClick = () => {
    const statusParams: any = {
      conversationID: conversationID,
      status: 1,
    };
    putAPIAsync(Provider.URLS.BASE_URL, Provider.API_URLS.UpdateConversationStatus, statusParams, AcceptMessageSuccess, onAPIFailure);
  };
  const markRead = (recipientUcnum: string) => {
    const params = {
      conversationID: conversationID,
      recipientUcnum: recipientUcnum,
    };
    putAPIAsync(Provider.URLS.BASE_URL, Provider.API_URLS.MarkRead, params, () => {
      windowPostMessage({ type: "updateNotification" }, WEBSITE_PATH);
      const params = {
        senderID: senderID,
        messageType: 4,
      };
      userConnection && userConnection.newMessage("true", JSON.stringify(params), decodeURIComponent(receiverID));
    });
  };
  const onRestartConversation = () => {
    const param = {
      conversationID: conversationID,
      isActive: true,
      ucnum: senderID,
    };
    deleteAPIAsync(Provider.URLS.BASE_URL, Provider.API_URLS.RestartConversation, param, () => {
      const paramRec = {
        conversationID: conversationID,
        isActive: true,
        ucnum: decodeURIComponent(receiverID),
      };
      deleteAPIAsync(Provider.URLS.BASE_URL, Provider.API_URLS.RestartConversation, paramRec);
    });
  };
  const updateReplyMessage = (messageDetails: MessageData) => {
    if (selectedReply) {
      const updateReplyParams = {
        messageID: messageDetails.messageID,
        parentID: selectedReply.messageID,
      };
      putAPIAsync(Provider.URLS.BASE_URL, Provider.API_URLS.UpdateMessageReply, updateReplyParams);
    }
    setSelectedReply(null);
  };
  const updateConversation = (messageDetails: MessageData) => {
    const updateParams: any = {
      conversationID: conversationID,
      lastActiveAt: 0,
      lastMessageId: messageDetails.messageID,
    };
    putAPIAsync(Provider.URLS.BASE_URL, Provider.API_URLS.UpdateConversation, updateParams);
    updateMessageStatus(messageDetails.messageID, 2);
    updateReplyMessage(messageDetails);
  };
  const insertMessage = (messageDetails: MessageData) => {
    const insertParams: any = {
      id: UniqueID(),
      messageID: messageDetails.messageID,
      messageContent: Encrypt(messageDetails.messageType === 3 || messageDetails.messageType === 4 ? JSON.stringify(messageDetails.messageContent) : messageDetails.messageContent),
      messageType: messageDetails.messageType,
      conversationID: conversationID,
      sentAt: 0,
      lastActivityTime: 0,
      isRead: false,
      parent: messageDetails.parent ? messageDetails.parent[0].messageID : null,
      recipientUcnum: decodeURIComponent(receiverID),
    };
    postAPIAsync(
      Provider.URLS.BASE_URL,
      Provider.API_URLS.InsertMessage,
      insertParams,
      () => updateConversation(messageDetails),
      () => updateMessageStatus(messageDetails.messageID, 1)
    );
  };
  const deleteMessage = (messageID: string, deletedBy: string, onSuccess: any) => {
    const params = {
      conversationID: conversationID,
      messageID: messageID,
      deletedBy: deletedBy,
    };
    deleteAPIAsync(Provider.URLS.BASE_URL, Provider.API_URLS.DeleteMessages, params, onSuccess, onAPIFailure);
  };

  const hardDeleteMessage = (messageID: string, deletedBy: string, onSuccess: any) => {
    const params = {
      conversationID: conversationID,
      messageID: messageID,
      deletedBy: deletedBy,
    };
    deleteAPIAsync(Provider.URLS.BASE_URL, Provider.API_URLS.MessageHardDelete, params, onSuccess, onAPIFailure);
  };

  const unblockConversation = () => {
    if (SELLER) {
      deleteAPIAsync(Provider.URLS.BASE_URL, Provider.API_URLS.StoreDeleteBlock, { ucnum: senderID, entityCodeNum: decodeURIComponent(receiverID), entityCodeType: 2 }, UnblockSuccess, onAPIFailure);
    } else {
      postAPIAsync(Provider.URLS.BASE_URL, Provider.API_URLS.DeleteBlock, { ucnum: senderID, entityCodeNum: decodeURIComponent(receiverID), entityCodeType: receiverDetails.entityType }, UnblockSuccess, onAPIFailure);
    }
  };
  const blockConversation = () => {
    if (SELLER) {
      postAPIAsync(Provider.URLS.BASE_URL, Provider.API_URLS.StoreInsertBlock, { ucnum: senderID, entityCodeNum: decodeURIComponent(receiverID), entityCodeType: 2 }, BlockSuccess, onAPIFailure);
    } else {
      postAPIAsync(Provider.URLS.BASE_URL, Provider.API_URLS.InsertBlock, { ucnum: senderID, entityCodeNum: decodeURIComponent(receiverID), entityCodeType: receiverDetails.entityType }, BlockSuccess, onAPIFailure);
    }
  };
  const updateFollowStatus = (statusType: number) => {
    const params = {
      ucnum: senderID,
      entityCodeNum: receiverID,
      entityCodeType: receiverDetails.entityType,
    };
    postAPIAsync(Provider.URLS.BASE_URL, Provider.API_URLS.InsertFollowers, params, () => FollowSuccess(statusType), onAPIFailure);
  };
  const onUpdateReaction = (messageID: string, key: string, reaction: any, isRemove: boolean = false) => {
    if (key === "7") {
      chatFooterRef && chatFooterRef.current && chatFooterRef.current.openEmoji();
      setReactionEmojiMessageID(messageID);
    } else {
      const allMessages: MessageData[] = [...messages];
      let currentMessage: MessageData | undefined = allMessages.find((el) => el.messageID === messageID);
      if (currentMessage) {
        if (isRemove) {
          currentMessage.reactions = currentMessage.reactions?.filter((el: any) => el.ucnum !== senderID);
        } else {
          if (currentMessage.reactions) {
            const foundReactionBySameUser: any = currentMessage.reactions.find((el: any) => el.ucnum === senderID);
            if (foundReactionBySameUser) {
              currentMessage.reactions = currentMessage.reactions.filter((el: any) => el !== foundReactionBySameUser);
            }
          }
          if (currentMessage.reactions) {
            currentMessage.reactions?.unshift({
              ucnum: senderID,
              reaction: Encrypt(reaction.nodeString),
            });
          } else {
            currentMessage.reactions = [
              {
                ucnum: senderID,
                reaction: Encrypt(reaction.nodeString),
              },
            ];
          }
        }
        const params: any = {
          messageID: messageID,
          messageReactions: currentMessage.reactions,
        };
        if (reaction || isRemove) {
          putAPIAsync(Provider.URLS.BASE_URL, Provider.API_URLS.UpdateMessageReaction, params, () => {
            if (currentMessage) {
              createAndSendMessage("", 11, currentMessage.reactions, messageID);
              const params = {
                senderID: senderID,
                messageType: 4,
              };
              userConnection && userConnection.newMessage("true", JSON.stringify(params), decodeURIComponent(receiverID));
              userConnection && userConnection.newMessage("true", JSON.stringify(params), senderID);
            }
            scrollRef.current = false;
            setMessages(allMessages);
            const updateParams: any = {
              conversationID: conversationID,
              lastActiveAt: 0,
              lastMessageId: !isRemove ? messageID : messages[0].messageID,
            };
            putAPIAsync(Provider.URLS.BASE_URL, Provider.API_URLS.UpdateConversation, updateParams);
          });
        }
      }
    }
  };
  const deleteChat = () => {
    const params = {
      ucnum: senderID,
      deleteConversations: [{ conversationID: conversationID, isActive: false }],
    };
    postAPIAsync(Provider.URLS.BASE_URL, Provider.API_URLS.DeleteConversation, params, DeleteSuccess, onAPIFailure);
  };
  const updateFooterHeight = (height: number) => {
    setChatFooterHeight(height);
  };
  // #endregion

  // #region use states
  useEffect(() => {
    if (!isMinimized) {
      markRead(senderID);
      createAndSendMessage("", 5);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMinimized]);
  useEffect(() => {
    if (selectedMessages.length > 0) {
      let menuData: any = null;
      if (selectedMessages[0].deletedBy === "Both") {
        menuData = [deleteMenuItem(() => onDeleteMessageClick(selectedMessages[0]))];
      } else {
        menuData = [optionsMenuItem(() => {}, [copyMenuItem(onCopyMessage)])];
        selectedMessages.length === 1 &&
          menuData.unshift(
            replyMenuItem(() => onReplyClick(selectedMessages[0])),
            deleteMenuItem(() => onDeleteMessageClick(selectedMessages[0]))
          );
      }
      setMobMenuOptions(menuData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMessages]);
  useEffect(() => {
    messagesRef.current = messages;
    if (currentMessage.current && !currentMessage.current.notSent) {
      storeMessageCount.current = storeMessageCount.current + 1;
      setIsMessageLimitReached();
      insertMessage(currentMessage.current);
      signalRMessage(currentMessage.current, true);
      currentMessage.current = null;
    }
    if (lastScrollPosition.current !== -1) {
      chatMessageRef.current.scrollChatToPosition(lastScrollPosition.current);
      lastScrollPosition.current = -1;
    }
    if (scrollRef.current && chatMessageRef.current) {
      chatMessageRef.current.scrollChatToBottom();
      scrollRef.current = false;
    }
    if (lastScrollElement.current) {
      chatMessageRef.current.scrollChatToPosition(lastScrollElement.current.offsetTop - 12);
      lastScrollElement.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);
  useEffect(() => {
    if (offset === 0) {
      scrollRef.current = true;
      // mark read in the database and notify the recepient
      markRead(senderID);
      createAndSendMessage("", 5);
    }
    getAPIAsync(Provider.URLS.BASE_URL, Provider.API_URLS.GetMessages, { conversationID: encodeURIComponent(conversationID), ucnum: encodeURIComponent(senderID), SIZE: SIZE, offset: offset }, GetMessagesSuccess, GetMessagesFailure);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);
  useEffect(() => {
    if (blockedAccount || (SELLER && receiverDetails.status === 0 && messages.length > 0 && !adminIsBlocked)) {
      const mac: any = document.getElementsByClassName("messageAcceptContainer")[0];
      if (mac) {
        updateFooterHeight(mac.offsetHeight);
      }
    } else if (adminIsBlocked && receiverDetails.entityType === 2) {
      updateFooterHeight(8);
    } else {
      updateFooterHeight(CHAT_FOOTER_START_HEIGHT);
    }
    if (blockedAccount) {
      chatMessageRef.current && chatMessageRef.current.setPadding(0);
    }
    if (chatOptionsItems.current) {
      chatOptionsItems.current =
        (!isNullOrEmpty(receiverDetails) && isNullOrEmpty(receiverDetails.name)) || adminIsBlocked
          ? [deleteMenuItem(onDeleteClick)]
          : [
              receiverDetails.pinnedAt && receiverDetails.pinnedAt > 0 ? unpinMenuItem(onUnpinClick) : pinMenuItem(onPinClick),
              receiverDetails.isMuted ? unmuteMenuItem(onUnmuteClick) : muteMenuItem(onMuteClick),
              deleteMenuItem(onDeleteClick),
              blockedAccount ? unblockMenuItem(onUnblockClick) : blockMenuItem(onBlockClick),
            ];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiverDetails]);
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      const cf: any = document.getElementsByClassName("chatFooter")[0];
      if (cf) {
        setChatFooterHeight(cf.offsetHeight);
      }
    });
    if (document.getElementsByClassName("chatFooter")[0]) {
      const config: any = { attributes: true, childList: true, characterData: true };
      resizeObserver.observe(document.getElementsByClassName("chatFooter")[0], config);
    }
    return () => resizeObserver.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    window.addEventListener("click", handleWindowClick);
    return () => {
      window.removeEventListener("click", handleWindowClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    isMinimized || blockedAccount || (adminIsBlocked && receiverDetails.entityType === 2) || (SELLER && receiverDetails.status === 0 && messages.length > 0) ? setChatFooterHeight(8) : setChatFooterHeight(CHAT_FOOTER_START_HEIGHT);
    window.addEventListener("message", handleWindowMessageReceived);
    window.addEventListener("click", (e) => {
      windowPostMessage({ type: "chatAnalytics", chatID: chatID }, WEBSITE_PATH);
    });
    return () => {
      window.removeEventListener("message", handleWindowMessageReceived);
      window.removeEventListener("click", (e) => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // #endregion

  return (
    <Box className={`chatHolder w-per-100 h-per-100 pos-rel`}>
      <Box className={`bg-true-white chatWindow ${isMinimized ? "chatWindow-minimized" : ""}`}>
        {/* ------------------ Chat Header start ------------------ */}
        {selectedMessages.length === 0 ? (
          <ChatHeader chatSelected={chatSelected} adminIsBlocked={adminIsBlocked} connectivityStatus={connectivityStatus} isTyping={isTyping} isMinimized={isMinimized} onCloseChat={onCloseChat} onChatMenuClick={onChatMenuClick} onMinimizeChat={onMinimizeChat} />
        ) : (
          <CreateMenuHeader ref={chatMenuHeaderRef} backCallback={() => setSelectedMessages([])} arrMenuOptions={mobMenuOptions} dataCount={selectedMessages.length} />
        )}
        {/* ------------------ Chat Header end ------------------ */}
        {/* ------------------ Chat Loader start ------------------ */}
        {loader ? <ChatSkeleton chatAreaHeight={chatFooterHeight} /> : null}
        {/* ------------------ Chat Loader end ------------------ */}
        {/* ------------------ Chat Body start ------------------ */}
        <ChatMessage
          ref={chatMessageRef}
          chatWarningRef={chatWarningRef}
          messages={messages}
          selectedMessages={selectedMessages}
          receiverID={receiverID}
          senderID={senderID}
          senderProfileDetails={senderProfileDetails}
          messageLimitReached={messageLimitReached}
          blockedAccount={blockedAccount}
          adminIsBlocked={adminIsBlocked}
          chatFooterHeight={chatFooterHeight}
          chatReactionRef={chatReactionRef}
          onScrollEvent={onScrollEvent}
          onMessageLongPress={onMessageLongPress}
          onWavingHandClick={onWavingHandClick}
          updateFollowStatus={updateFollowStatus}
          onUpdateReaction={onUpdateReaction}
          clearSelectedMessages={clearSelectedMessages}
          onReplyClick={onReplyClick}
          onDeleteMessageClick={onDeleteMessageClick}
          onMessageSingleClick={onMessageSingleClick}
          isAllowedToSendMessage={isAllowedToSendMessage}
        />
        {isMinimized && newMessageCount > 0 ? <Badge className="minimizedUnreadBadge" badgeContent={newMessageCount} color="error"></Badge> : null}
        {!isMinimized && !isChatAtBottom ? (
          <IconButton className="scrolltobottom" onClick={onScrollToBottomClick} sx={{ bottom: chatFooterHeight + 8 }}>
            {newMessageCount > 0 ? (
              <Badge badgeContent={newMessageCount} color="error">
                {icon({ iconName: "stat_minus_2" })}
              </Badge>
            ) : (
              icon({ iconName: "stat_minus_2" })
            )}
          </IconButton>
        ) : null}
        {/* ------------------ Chat Body end ------------------ */}
        {/* ------------------ Blocked Account start ------------------ */}
        {!isMinimized && blockedAccount ? <BlockedAccount userName={receiverDetails.name} onDeleteClick={onDeleteClick} onUnblockClick={onUnblockClick} updateFooterHeight={updateFooterHeight} /> : null}
        {/* ------------------ Blocked Account end ------------------ */}
        {SELLER && receiverDetails.status === 0 && messages.length > 0 && !blockedAccount && !adminIsBlocked ? <MessageAccept onDeleteClick={onDeleteClick} onBlockClick={onBlockClick} onAcceptClick={onAcceptMessageClick} updateFooterHeight={updateFooterHeight} /> : null}
        {/* ------------------ Chat Footer start ------------------ */}
        {isMinimized || blockedAccount || (adminIsBlocked && receiverDetails.entityType === 2) || (SELLER && receiverDetails.status === 0 && messages.length > 0 && !adminIsBlocked) ? null : (
          <ChatFooter
            ref={chatFooterRef}
            senderID={senderID}
            ucnum={ucnum}
            loader={loader}
            disabled={(SELLER && adminIsBlocked) || (!isNullOrEmpty(receiverDetails) && isNullOrEmpty(receiverDetails.name))}
            reactionEmojiMessageID={reactionEmojiMessageID}
            shareDialogRef={shareDialogRef}
            selectedReply={selectedReply}
            onSendClick={onSendClick}
            onThumbsUpClick={onThumbsUpClick}
            onBookmarkedPostClick={onBookmarkedPostClick}
            onMyPostClick={onMyPostClick}
            onReactionEmojiClick={onReactionEmojiClick}
            clearReactionEmojiMessageID={clearReactionEmojiMessageID}
            onReplyCloseClick={onReplyCloseClick}
            onMessageBoxChanged={onMessageBoxChanged}
          />
        )}
        {/* ------------------ Chat Footer end ------------------ */}
      </Box>
      {chatOptions && <CreateMenu anchorElement={chatOptions} placement="bottom-start" setAnchorElement={setChatOptions} menuItemList={chatOptionsItems.current} handleClose={onChatOptionsClose} ref={chatOptionsRef} />}
    </Box>
  );
};

export default ChatWindow;
